// Where a node lives on the configuration map, written like `pwd`:
// `ORG:mfam | REPO:Promise686/fam.aviary | AGENT:ClaudeCode`.
//
// Pure — no React, no API client — so it is unit-testable on its own.

import type { GraphNode, GraphResponse } from "@/types/api";

/** Edges that mean "lives inside". The rest — an agent *uses* a tool, *reads* a
 * knowledge base, *runs on* a model — are references, and following them would
 * give a tool shared by three agents three different addresses. */
const CONTAINMENT = new Set([
  "contains",
  "owns",
  "governs",
  "configures",
  "defines",
  "has_skill",
  "trusts",
  "provides",
]);

/** The path segment name for each kind — the `ORG:` / `REPO:` / `AGENT:` part.
 * Upper-case so the field types stand apart from the names beside them. */
const PATH_PREFIX: Record<string, string> = {
  tenant: "ORG",
  domain: "DOMAIN",
  agent: "AGENT",
  skill: "SKILL",
  tool: "TOOL",
  knowledge_base: "KB",
  llm_model: "MODEL",
  trusted_system: "TRUSTED",
  github_repo: "REPO",
  // Claude Code and Cursor are the coding agents a repository configures.
  claude_code: "AGENT",
  cursor: "AGENT",
  repo_agent: "SUBAGENT",
  repo_skill: "SKILL",
  repo_command: "COMMAND",
  repo_rule: "RULE",
  cursor_agent: "SUBAGENT",
  cursor_skill: "SKILL",
  cursor_command: "COMMAND",
};

/** Spaced so the path reads at a glance rather than as one run of text. */
const SEPARATOR = " | ";

/** The node's ancestors, root first, ending with the node itself. */
export function ancestry(data: GraphResponse, nodeId: string): GraphNode[] {
  const byId = new Map(data.nodes.map((n) => [n.id, n]));
  const parent = new Map<string, string>();
  for (const e of data.edges) {
    // First containment edge wins: every node has exactly one owner today, and
    // a deterministic choice beats a path that changes between renders.
    if (CONTAINMENT.has(e.kind) && !parent.has(e.target)) parent.set(e.target, e.source);
  }
  const chain: GraphNode[] = [];
  const seen = new Set<string>();
  let current: string | undefined = nodeId;
  while (current && !seen.has(current)) {
    seen.add(current);
    const node = byId.get(current);
    if (!node) break;
    chain.unshift(node);
    current = parent.get(current);
  }
  return chain;
}

/** A node's address, the way `pwd` names a directory. Spaces inside a name are
 * dropped ("Claude Code" becomes `AGENT:ClaudeCode`) so the only spaces left
 * are the ones around the separator. */
export function nodePath(data: GraphResponse, nodeId: string): string {
  return ancestry(data, nodeId)
    .map((n) => `${PATH_PREFIX[n.kind] ?? n.kind.toUpperCase()}:${n.label.replace(/\s+/g, "")}`)
    .join(SEPARATOR);
}
